import {
  trainingLearnerView,
  type TrainingAssignment,
  type TrainingLanguage,
  type TrainingLearnerStatus,
  type TrainingLearnerView,
} from '@hrms/shared';
import { compareDueAtNullsLast } from '../training/trainingStatus.js';
import { localizeText, readRememberedTrainingLanguage } from './player/lessonLanguage.js';

/**
 * My learning's rules as pure functions (PLAN §4.1, R10): which tab a course sits in, in what
 * order, and what its card says — so the page, the Home card and the tests agree.
 */

export interface LearnerCourse {
  assignment: TrainingAssignment;
  view: TrainingLearnerView;
}

export type LearnerTabKey = 'in_progress' | 'not_started' | 'completed' | 'not_completed';

export const LEARNER_TAB_LABELS: Record<LearnerTabKey, string> = {
  in_progress: 'In progress',
  not_started: 'Not started',
  completed: 'Completed',
  not_completed: 'Not completed',
};

const time = (iso: string | null) => (iso ? new Date(iso).getTime() : 0);

/**
 * Assignments split into the learner's four states; cancelled ones are hidden (PLAN §5). Each course's
 * title and description come in `language` where it has them (ADR 0039) — by default the one the
 * learner last picked in the player, read best-effort from this browser.
 */
export function bucketLearnerCourses(
  assignments: readonly TrainingAssignment[],
  now: Date,
  language: TrainingLanguage | null = readRememberedTrainingLanguage(),
): Record<LearnerTabKey, LearnerCourse[]> {
  const buckets: Record<LearnerTabKey, LearnerCourse[]> = {
    in_progress: [],
    not_started: [],
    completed: [],
    not_completed: [],
  };
  for (const assignment of assignments) {
    const view = trainingLearnerView(
      {
        status: assignment.status,
        dueAt: assignment.dueAt ? new Date(assignment.dueAt) : null,
        overrunMinutes: assignment.overrunMinutes,
      },
      now,
    );
    if (view.status === null) continue;
    const task = localizeText(assignment.task, language);
    buckets[view.status].push({ assignment: task === assignment.task ? assignment : { ...assignment, task }, view });
  }
  // Most recently touched first — what you were just doing is what you want to see.
  buckets.in_progress.sort(
    (a, b) =>
      time(b.assignment.lastActivityAt ?? b.assignment.startedAt) -
        time(a.assignment.lastActivityAt ?? a.assignment.startedAt) ||
      compareDueAtNullsLast(a.assignment, b.assignment),
  );
  // Earliest deadline first; no deadline last.
  buckets.not_started.sort((a, b) => compareDueAtNullsLast(a.assignment, b.assignment));
  buckets.completed.sort((a, b) => time(b.assignment.completedAt) - time(a.assignment.completedAt));
  buckets.not_completed.sort((a, b) => time(b.assignment.createdAt) - time(a.assignment.createdAt));
  return buckets;
}

/** The tab that opens first: where there is something to do, else what is finished. */
export function defaultLearnerTab(buckets: Record<LearnerTabKey, LearnerCourse[]>): LearnerTabKey {
  if (buckets.in_progress.length > 0) return 'in_progress';
  if (buckets.not_started.length > 0) return 'not_started';
  if (buckets.completed.length > 0) return 'completed';
  return buckets.not_completed.length > 0 ? 'not_completed' : 'in_progress';
}

/** The card's one action (fixes E9: a started course says Continue, never "Review"). */
export function learnerActionLabel(status: TrainingLearnerStatus): string {
  switch (status) {
    case 'not_started':
      return 'Start';
    case 'in_progress':
      return 'Continue';
    case 'completed':
      return 'Review';
    case 'not_completed':
      return 'View';
  }
}

/** Where a course card leads: the player by assignment id (it rewrites to the readable slug). */
export function learnerCoursePath(assignmentId: string): string {
  return `/learning/${assignmentId}`;
}

/** "Due in 3 days" / "Due today" / "Overdue by 2 days" / null — the hero's due phrase. */
export function dueInWords(dueAt: string | null, now: Date): string | null {
  if (!dueAt) return null;
  const ms = new Date(dueAt).getTime() - now.getTime();
  const hours = Math.round(Math.abs(ms) / 3_600_000);
  const days = Math.round(Math.abs(ms) / 86_400_000);
  if (ms < 0) {
    if (hours < 24) return `Overdue by ${Math.max(1, hours)} ${hours <= 1 ? 'hour' : 'hours'}`;
    return `Overdue by ${days} ${days === 1 ? 'day' : 'days'}`;
  }
  if (hours < 24) return hours <= 1 ? 'Due within the hour' : `Due in ${hours} hours`;
  return `Due in ${days} ${days === 1 ? 'day' : 'days'}`;
}
